import { flattenDeep, mapKeys, pickBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { isArrayLike, toArray } from './Array';
import { Duration } from './Duration';
import { FormFields, IFormField, PrimitiveFormField } from './PrimitiveFormField';
import { Nullable, Readonly } from './Types';

export class ObjectFormField<T> implements IFormField<T> {
    public constructor(name: string, value: Nullable<T>) {
        console.assert(value, 'Value must exist');

        this.name = name;

        // eslint-disable-next-line
        for (const key in value) {
            const v = value[key];

            if (v && typeof v == 'object' && !(v instanceof Date) && !(v instanceof Duration) && !isArrayLike(v)) {
                this.formFields.set(key, new ObjectFormField(key, v as any));
            } else {
                this.formFields.set(key, new PrimitiveFormField(key, v));
            }
        }
    }

    @action
    public validate() {
        this.formFields.forEach(f => f.validate());

        const passes = !toArray(this.formFields).find(f => f.errorState == 'danger' || f.errorState == 'warning');

        return passes;
    }

    @computed
    public get fields(): FormFields<T> {
        const fields: any = {};
        this.formFields.forEach((field, key) => (fields[key] = field));
        return fields;
    }

    @computed
    public get value(): Readonly<T> {
        const fields: any = {};
        this.formFields.forEach((field, key) => (fields[key] = field.value));
        return fields;
    }

    @action
    public addErrors(errors: { [key: string]: string[] } | string[]) {
        if (isArrayLike(errors)) {
            throw new Error('Unsupported method');
        }

        console.debug('ObjectFormField errors', errors);

        for (const key in errors) {
            if (key.includes('.')) {
                const objName = key.split('.')[0];

                const e1 = pickBy(errors, (_, k) => k.startsWith(objName));
                const e2 = mapKeys(e1, (_, k) => k.substr(objName.length + 1));

                const f = this.formFields.get(objName);
                if (f) {
                    f.addErrors(e2 as any);
                }
            } else {
                const f = this.formFields.get(key);
                if (f) {
                    f.addErrors(errors[key]);
                }
            }
        }
    }

    @action
    public setValue(value: T) {
        // eslint-disable-next-line
        for (const key in value) {
            const v = (value as any)[key];
            const f = this.formFields.get(key);
            if (f) {
                f.setValue(v);
            } else {
                this.formFields.set(key, new PrimitiveFormField(key, v));
            }
        }
    }

    @action
    public clear() {
        this.formFields.forEach(f => f.clear());
    }

    @action
    public add<V>(key: string, value: V) {
        this.formFields.set(key, new PrimitiveFormField(key, value));
    }

    @action
    public remove(key: string) {
        this.formFields.delete(key);
    }

    @action
    public onSubmit() {
        this.formFields.forEach(f => f.onSubmit());
    }

    public onMount(validation: string): void {
        throw new Error('Unsupported Method');
    }

    public onChange(value: T): void {
        throw new Error('Unsupported Method');
    }

    @computed
    public get state() {
        return this.submitted ? this.errorState : undefined;
    }

    @computed
    public get touched(): boolean {
        return !!toArray(this.formFields).find(f => f.touched);
    }

    @computed
    public get dirty(): boolean {
        return !!toArray(this.formFields).find(f => f.dirty);
    }

    @computed
    public get errorState(): 'success' | 'warning' | 'danger' | undefined {
        const fields = toArray(this.formFields);
        const danger = fields.find(f => f.errorState == 'danger');
        const warning = fields.find(f => f.errorState == 'warning');
        const success = fields.find(f => f.errorState == 'success');

        if (danger) return 'danger';
        if (warning) return 'warning';
        if (success) return 'success';
    }

    @computed
    public get errors(): string[] {
        return flattenDeep(toArray(this.formFields).map(f => f.errors)) as string[];
    }

    @observable
    public name: string;

    @observable
    public submitted = false;

    @observable
    private readonly formFields = new Map<string, IFormField<any>>();
}
